/**
 * services/sync/SyncInitializationService.ts
 * Singleton service for sync initialization on login
 *
 * Orchestrates:
 * - User preference queries
 * - Provider-specific connection checks (via factory)
 * - Timeout protection (10s, with proper cleanup)
 * - Idempotency (concurrent call handling)
 * - Error detection and categorization (structured errors)
 */

import { userSyncSettingsService } from '@/services/UserSyncSettingsService';
import * as providerFactory from './providerFactory';
import { GoogleDriveSyncProvider } from './GoogleDriveSyncProvider';
import { BackgroundSyncService } from './BackgroundSyncService';
import { isOnline } from '@/utils/connectionUtils';
import { getProviderDisplayName } from '@/utils/syncProviderUtils';
import { getOrCreateDeviceId } from '@/utils/deviceDetection';
import {
  SYNC_INIT_TIMEOUT_MS,
  SYNC_INIT_BACKOFF_MS,
  SYNC_INIT_MAX_RETRIES,
} from '@/config/sync/syncInit.config';
import type { UserSyncSettings } from '@/types/supabase/UserSyncSettings';
import type {
  ConnectionResult,
  SyncInitOptions,
  SyncInitReason,
} from '@/types/sync/SyncInitTypes';
import { getRequiredModal, shouldRetryReason } from '@/utils/syncInitPolicy';
import { generateUUID } from '@/utils/uuid';

// ===== SERVICE CLASS =====

class SyncInitializationService {
  private static instance: SyncInitializationService | null = null;

  // Idempotency: prevent concurrent initialization for same user
  // Stores promise with its abort signal to detect stale aborted entries
  private inFlightInits: Map<string, { promise: Promise<ConnectionResult>; signal?: AbortSignal }> = new Map();

  /**
   * Private constructor (singleton pattern)
   */
  private constructor() {}

  /**
   * Get singleton instance
   */
  static getInstance(): SyncInitializationService {
    if (!SyncInitializationService.instance) {
      SyncInitializationService.instance = new SyncInitializationService();
    }
    return SyncInitializationService.instance;
  }

  // ===== PUBLIC API =====

  /**
   * Initialize sync on login for given user
   *
   * Features:
   * - Idempotent: concurrent calls for same user return same promise
   * - Respects syncOnLogin flag (unless force=true)
   * - 10s timeout on provider.isConnected() to detect hangs
   * - Correlation ID logging (userId hashed for privacy)
   *
   * @param userId - User ID
   * @param options - Optional configuration
   * @returns ConnectionResult with provider or error details
   */
  async initializeOnLogin(
    userId: string,
    options?: SyncInitOptions
  ): Promise<ConnectionResult> {
    // Generate correlation ID for log tracing
    const syncInitId = generateUUID().slice(0, 8);
    const userIdShort = userId.slice(0, 8);
    const startTime = Date.now();

    console.log(`[${syncInitId}] 🔄 Initializing sync for user ${userIdShort}`);

    // Fast-path: if signal is already aborted, return immediately (avoids caching doomed entry)
    if (options?.signal?.aborted) {
      console.log(`[${syncInitId}] ⏹️ Signal already aborted, skipping initialization`);
      return { success: false, reason: 'ABORTED' };
    }

    // Idempotency: return in-flight promise if already initializing
    const existing = this.inFlightInits.get(userId);
    if (existing) {
      // Check if the existing entry's signal was aborted (StrictMode cleanup)
      // If so, delete it and proceed with fresh init
      if (existing.signal?.aborted) {
        console.log(
          `[${syncInitId}] 🔄 Existing init was aborted, starting fresh for user ${userIdShort}`
        );
        this.inFlightInits.delete(userId);
      } else {
        console.log(
          `[${syncInitId}] ⏸️ Already initializing for user ${userIdShort}, returning existing promise`
        );
        return existing.promise;
      }
    }

    // Start new initialization
    const initPromise = this.performInitialization(
      userId,
      options,
      syncInitId,
      userIdShort,
      startTime
    );

    // Cache promise with its signal for concurrent call handling
    // Only include signal property if it exists (exactOptionalPropertyTypes compliance)
    const cacheEntry: { promise: Promise<ConnectionResult>; signal?: AbortSignal } = { promise: initPromise };
    if (options?.signal) {
      cacheEntry.signal = options.signal;
    }
    this.inFlightInits.set(userId, cacheEntry);

    // Early eviction on abort (handles StrictMode unmount before promise settles)
    options?.signal?.addEventListener(
      'abort',
      () => this.inFlightInits.delete(userId),
      { once: true }
    );

    // Cleanup after completion (success or failure) for non-abort paths
    initPromise.finally(() => {
      this.inFlightInits.delete(userId);
    });

    return initPromise;
  }

  /**
   * Handle reconnection for specific provider
   * Used after user resolves modal (re-authenticates)
   *
   * @param userId - User ID
   * @param provider - Provider to reconnect
   * @returns Success/failure result
   */
  async handleReconnection(
    userId: string,
    provider: 'google-drive' | 'local'
  ): Promise<{ success: boolean; error?: string }> {
    const syncInitId = generateUUID().slice(0, 8);
    const userIdShort = userId.slice(0, 8);

    console.log(
      `[${syncInitId}] 🔄 Handling reconnection for ${provider} (user ${userIdShort})`
    );

    try {
      // Force initialization (bypass syncOnLogin check)
      const result = await this.initializeOnLogin(userId, { force: true });

      if (result.success) {
        console.log(`[${syncInitId}] ✅ Reconnection successful`);
        return { success: true };
      } else {
        console.log(`[${syncInitId}] ❌ Reconnection failed: ${result.reason}`);
        return { success: false, error: result.error ?? 'Reconnection failed' };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[${syncInitId}] ❌ Reconnection error:`, errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  /**
   * Initialize sync engine after onboarding provider selection
   *
   * Called by StorageProviderStep after user connects a provider (Google Drive or Local Directory).
   * This method is the single owner of sync engine lifecycle during onboarding.
   *
   * Features:
   * - Idempotent: Returns early if sync engine is already running
   * - Non-blocking: Errors are logged but not thrown (onboarding continues)
   * - No-op for Skip: Returns early if providerKey is null
   *
   * @param userId - User ID for sync engine initialization
   * @param providerKey - Provider type ('google-drive' | 'local') or null for Skip
   */
  async initializeAfterOnboarding(
    userId: string,
    providerKey: UserSyncSettings['activeSyncProvider']
  ): Promise<void> {
    const syncInitId = generateUUID().slice(0, 8);
    const userIdShort = userId.slice(0, 8);

    console.log(
      `[${syncInitId}] 🚀 initializeAfterOnboarding called (user: ${userIdShort}, provider: ${providerKey ?? 'null'})`
    );

    // Early return for Skip case (null provider)
    if (!providerKey) {
      console.log(`[${syncInitId}] ⏸️ No provider selected (Skip), skipping sync initialization`);
      return;
    }

    // Idempotency check: skip if sync engine is already running
    const syncService = BackgroundSyncService.getInstance();
    if (syncService.getIsRunning()) {
      console.log(`[${syncInitId}] ⏸️ Sync engine already running, skipping initialization`);
      return;
    }

    try {
      // Create provider instance via factory
      const provider = providerFactory.createProvider(providerKey);
      if (!provider) {
        console.warn(`[${syncInitId}] ⚠️ Failed to create provider for '${providerKey}', skipping sync initialization`);
        return;
      }

      // Get device ID for sync engine
      const deviceId = getOrCreateDeviceId();

      // Initialize BackgroundSyncService
      console.log(`[${syncInitId}] 🔧 Initializing BackgroundSyncService...`);
      await syncService.initialize(userId, deviceId);
      syncService.setProvider(provider);

      // Start sync service
      const started = await syncService.start();
      if (!started) {
        console.warn(`[${syncInitId}] ⚠️ BackgroundSyncService.start() returned false`);
        return;
      }

      // Trigger initial sync
      console.log(`[${syncInitId}] 🔄 Triggering initial sync...`);
      await syncService.syncNow();

      console.log(`[${syncInitId}] ✅ Sync initialization complete`);
    } catch (error) {
      // Non-blocking: log error but don't throw (user continues onboarding)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(
        `[${syncInitId}] ❌ Sync initialization failed (non-blocking):`,
        errorMessage,
        { userId: userIdShort, providerKey }
      );
    }
  }

  // ===== PRIVATE HELPERS =====

  /**
   * Perform initialization logic (internal)
   */
  private async performInitialization(
    userId: string,
    options: SyncInitOptions | undefined,
    syncInitId: string,
    userIdShort: string,
    startTime: number
  ): Promise<ConnectionResult> {
    try {
      // Step 1: Query user sync preferences
      console.log(`[${syncInitId}] 📋 Fetching user sync settings...`);
      const settings = await userSyncSettingsService.getOrCreateSettings(userId);

      // Step 2: Check syncOnLogin flag (unless force=true)
      if (!options?.force && !settings.syncOnLogin) {
        console.log(`[${syncInitId}] ⏸️ syncOnLogin disabled, skipping initialization`);
        const durationMs = Date.now() - startTime;
        console.log(`[${syncInitId}] ✅ Completed in ${durationMs}ms (skipped)`);
        return { success: true };
      }

      // Step 3: Check if provider is configured
      if (!settings.activeSyncProvider) {
        console.log(`[${syncInitId}] ⏸️ No active sync provider, skipping initialization`);
        const durationMs = Date.now() - startTime;
        console.log(`[${syncInitId}] ✅ Completed in ${durationMs}ms (no provider)`);
        return { success: true };
      }

      // Step 3.5: Check if sync is enabled (user may have disconnected)
      // Note: This check is NOT bypassed by force=true - if user explicitly disconnected,
      // we should not auto-reconnect. Manual reconnection sets syncEnabled=true first.
      if (!settings.syncEnabled) {
        console.log(`[${syncInitId}] ⏸️ Sync not enabled (disconnected), skipping initialization`);
        const durationMs = Date.now() - startTime;
        console.log(`[${syncInitId}] ✅ Completed in ${durationMs}ms (not enabled)`);
        return { success: true };
      }

      // Step 4: Check network connectivity (SSR-safe)
      if (!isOnline()) {
        console.log(`[${syncInitId}] 🌐 Offline, cannot initialize sync`);
        return {
          success: false,
          reason: 'OFFLINE',
          error: 'No network connection available',
          shouldRetry: true, // Retry-able error
        };
      }

      // Step 5: Provider-specific initialization
      console.log(
        `[${syncInitId}] 🔌 Checking ${settings.activeSyncProvider} connection...`
      );
      const result = await this.initializeProvider(settings, syncInitId, options?.signal);

      // Step 6: Log result with telemetry
      const durationMs = Date.now() - startTime;
      const outcome = result.success ? 'success' : 'fail';

      console.debug('sync:init', {
        syncInitId,
        userIdShort,
        provider: settings.activeSyncProvider,
        outcome,
        reason: result.success ? null : result.reason,
        durationMs,
      });

      if (result.success) {
        console.log(`[${syncInitId}] ✅ Sync initialized successfully in ${durationMs}ms`);
      } else {
        console.log(
          `[${syncInitId}] ❌ Sync initialization failed in ${durationMs}ms - ${result.reason}`
        );
      }

      return result;
    } catch (error) {
      const durationMs = Date.now() - startTime;
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error(`[${syncInitId}] ❌ Initialization error in ${durationMs}ms:`, errorMessage);

      return {
        success: false,
        reason: 'UNKNOWN_ERROR',
        error: errorMessage,
        shouldRetry: false, // Don't retry unknown errors
      };
    }
  }

  /**
   * Initialize provider-specific connection (updated for 2.B)
   *
   * Delegates to provider-specific handlers wrapped in retry logic:
   * - Google Drive: needsNetwork=true (offline short-circuit)
   * - Local Directory: needsNetwork=false (local FS access)
   */
  private async initializeProvider(
    settings: UserSyncSettings,
    syncInitId: string,
    signal?: AbortSignal
  ): Promise<ConnectionResult> {
    const userId = settings.userId;

    // Delegate to provider-specific handler with retry logic
    switch (settings.activeSyncProvider) {
      case 'google-drive':
        return this.retryWithBackoff(
          () => this.autoConnectGoogleDrive(userId, syncInitId, signal),
          {
            ...(signal && { signal }),
            userId,
            provider: 'google-drive',
            needsNetwork: true, // Google Drive requires network
          }
        );

      case 'local':
        return this.retryWithBackoff(
          () => this.autoConnectLocalDirectory(userId, syncInitId, signal),
          {
            ...(signal && { signal }),
            userId,
            provider: 'local',
            needsNetwork: false, // Local Directory is File System Access (no network needed)
          }
        );

      default:
        // Unknown/unsupported provider → soft no-op (don't block login)
        console.warn(
          `[${syncInitId}] ⚠️ Unknown provider '${settings.activeSyncProvider}', skipping`
        );
        return { success: true };
    }
  }

  /**
   * Handle connection errors and categorize them (enhanced taxonomy)
   *
   * Complete status → reason mapping:
   * - 401/403 → TOKEN_REVOKED
   * - 419/440 → TOKEN_EXPIRED
   * - 404 (Drive) → RECONNECT_GOOGLE_DRIVE (folder missing)
   * - 404 (Local) → DIRECTORY_INVALID (directory moved)
   * - 429 → RATE_LIMITED (retryable)
   * - 5xx → PROVIDER_OUTAGE (retryable)
   * - DOMException:AbortError → NETWORK_ERROR
   * - CORS/fetch failures → NETWORK_ERROR
   * - Timeout → NETWORK_ERROR
   */
  private handleConnectionError(
    error: unknown,
    provider: 'google-drive' | 'local',
    syncInitId: string
  ): ConnectionResult {
    // Prefer structured errors from BFF/provider
    const anyErr = error as {
      message?: string;
      status?: number;
      code?: string;
      name?: string;
      retryAfter?: number; // Retry-After header (seconds) for 429
    };
    const msg = anyErr?.message ?? 'Unknown error';
    const httpStatus = anyErr?.status;

    console.error(`[${syncInitId}] ❌ ${provider} connection error:`, msg);

    // Categorize error (prioritize structured status over message sniffing)
    let reason: SyncInitReason = 'UNKNOWN_ERROR';
    let shouldRetry = false;

    // Check for ABORTED (cancellation, non-modal/non-error UX)
    if (msg === 'ABORTED' || (error instanceof DOMException && error.name === 'AbortError')) {
      reason = 'ABORTED';
      shouldRetry = false;
    }
    // Check for File System Access API errors (Local Directory only)
    // Different browsers throw different DOMException names for FS errors
    else if (
      error instanceof DOMException &&
      (error.name === 'NotAllowedError' || // Permission denied
        error.name === 'NotFoundError' || // Directory not found
        error.name === 'SecurityError') && // Security/CORS violation
      provider === 'local'
    ) {
      reason = 'DIRECTORY_INVALID';
      shouldRetry = false;
    }
    // Structured HTTP status (preferred) - complete taxonomy
    else if (httpStatus === 401 || httpStatus === 403) {
      reason = 'TOKEN_REVOKED';
      shouldRetry = false;
    } else if (httpStatus === 419 || httpStatus === 440) {
      reason = 'TOKEN_EXPIRED';
      shouldRetry = false;
    } else if (httpStatus === 404) {
      // 404: Drive folder missing vs Local directory invalid
      if (provider === 'google-drive') {
        // Google Drive folder missing (but tokens may be valid) - needs folder re-selection
        // Explicitly set modal here (don't rely on getRequiredModal for 404)
        return {
          success: false,
          reason: 'UNKNOWN_ERROR',
          modalRequired: 'RECONNECT_GOOGLE_DRIVE',
          error: msg,
          shouldRetry: false,
        };
      } else {
        // Local directory moved/deleted
        reason = 'DIRECTORY_INVALID';
      }
      shouldRetry = false;
    } else if (httpStatus === 429) {
      // Rate limited - retryable with backoff (or Retry-After if provided)
      reason = 'PROVIDER_OUTAGE'; // RATE_LIMITED not in enum, use PROVIDER_OUTAGE as proxy
      shouldRetry = true; // Retry-able
      const retryAfterMs = anyErr?.retryAfter ? anyErr.retryAfter * 1000 : undefined;
      return {
        success: false,
        reason,
        error: msg,
        shouldRetry,
        ...(retryAfterMs && { retryAfterMs }),
      };
    } else if (httpStatus && httpStatus >= 500) {
      reason = 'PROVIDER_OUTAGE';
      shouldRetry = true; // Retry-able error
    }
    // Message-based fallbacks (when no structured status available)
    else if (/TIMEOUT/i.test(msg)) {
      reason = 'PROVIDER_OUTAGE';
      shouldRetry = true; // Retry-able error
    } else if (/Unauthorized/i.test(msg)) {
      reason = 'TOKEN_REVOKED';
      shouldRetry = false;
    } else if (/NetworkError|network|CORS|fetch failed/i.test(msg)) {
      reason = 'UNKNOWN_ERROR'; // NETWORK_ERROR not in SyncInitReason enum, map to UNKNOWN_ERROR
      shouldRetry = true; // Retry-able
    } else if (!isOnline()) {
      // Offline check as final fallback
      reason = 'OFFLINE';
      shouldRetry = true;
    }

    // Get modal type from helper (or undefined for non-modal errors)
    const modalRequired = getRequiredModal(reason, provider);

    return {
      success: false,
      reason,
      ...(modalRequired && { modalRequired }),
      error: msg,
      shouldRetry,
    };
  }

  /**
   * Auto-connect Google Drive provider
   *
   * Features:
   * - Offline short-circuit FIRST (requires network)
   * - Token validation via isConnected()
   * - Silent refresh attempt (stub for 2.B)
   * - Enhanced error categorization via error.status
   *
   * @param userId - User ID
   * @param syncInitId - Correlation ID for logging
   * @param signal - Optional abort signal
   * @returns ConnectionResult
   */
  private async autoConnectGoogleDrive(
    userId: string,
    syncInitId: string,
    signal?: AbortSignal
  ): Promise<ConnectionResult> {
    const userIdShort = userId.slice(0, 8);

    // Offline short-circuit (Google Drive requires network)
    if (!isOnline()) {
      console.log(`[${syncInitId}] 🌐 Offline, cannot connect to Google Drive`);
      return { success: false, reason: 'OFFLINE', shouldRetry: true };
    }

    const provider = providerFactory.createProvider('google-drive');
    if (!provider) {
      console.warn(`[${syncInitId}] ⚠️ Failed to create Google Drive provider`);
      return { success: true }; // Unknown provider, soft no-op
    }

    try {
      // Check connection with timeout
      const connected = await this.withTimeout(
        provider.isConnected(),
        SYNC_INIT_TIMEOUT_MS,
        signal
      );

      if (connected) {
        const providerName = getProviderDisplayName('google-drive');
        console.log(`[${syncInitId}] ✅ ${providerName} connected (user: ${userIdShort})`);
        return { success: true, provider };
      }

      // Not connected - token issue
      // Try silent refresh (stub for 2.B)
      // TODO: Prefer factory narrowing (e.g., createProvider<'google-drive'>()) over 'as' cast
      const refreshed = await this.trySilentRefresh(provider as GoogleDriveSyncProvider);
      if (refreshed) {
        // Re-check after refresh
        const reconnected = await this.withTimeout(
          provider.isConnected(),
          SYNC_INIT_TIMEOUT_MS,
          signal
        );
        if (reconnected) {
          console.log(`[${syncInitId}] ✅ Google Drive reconnected after refresh`);
          return { success: true, provider };
        }
      }

      // Token expired/revoked
      console.log(`[${syncInitId}] 🔑 Google Drive token expired/revoked`);
      return {
        success: false,
        reason: 'TOKEN_EXPIRED',
        modalRequired: 'RECONNECT_GOOGLE_DRIVE',
        error: 'Google Drive connection expired',
        shouldRetry: false,
      };
    } catch (error) {
      return this.handleConnectionError(error, 'google-drive', syncInitId);
    }
  }

  /**
   * Auto-connect Local Directory provider
   *
   * Features:
   * - NO network check (File System Access is local)
   * - Directory permission/existence validation
   *
   * @param userId - User ID
   * @param syncInitId - Correlation ID for logging
   * @param signal - Optional abort signal
   * @returns ConnectionResult
   */
  private async autoConnectLocalDirectory(
    userId: string,
    syncInitId: string,
    signal?: AbortSignal
  ): Promise<ConnectionResult> {
    const userIdShort = userId.slice(0, 8);

    const provider = providerFactory.createProvider('local');
    if (!provider) {
      console.warn(`[${syncInitId}] ⚠️ Failed to create Local Directory provider`);
      return { success: true }; // Unknown provider, soft no-op
    }

    try {
      // Check connection with timeout (no network needed)
      const connected = await this.withTimeout(
        provider.isConnected(),
        SYNC_INIT_TIMEOUT_MS,
        signal
      );

      if (connected) {
        const providerName = getProviderDisplayName('local');
        console.log(`[${syncInitId}] ✅ ${providerName} connected (user: ${userIdShort})`);
        return { success: true, provider };
      }

      // Directory invalid (moved/deleted/permission lost)
      console.log(`[${syncInitId}] 📁 Local Directory not accessible`);
      return {
        success: false,
        reason: 'DIRECTORY_INVALID',
        modalRequired: 'RECONNECT_LOCAL_DIRECTORY',
        error: 'Local sync directory no longer accessible',
        shouldRetry: false,
      };
    } catch (error) {
      return this.handleConnectionError(error, 'local', syncInitId);
    }
  }

  /**
   * Wrap promise with timeout protection (properly clears timer on settle)
   *
   * Abort handling takes priority over timeout:
   * - If signal is already aborted, rejects immediately with ABORTED
   * - If signal aborts during execution, rejects with ABORTED (not TIMEOUT)
   * - Timer and listeners are cleaned up on all exit paths
   *
   * @param promise - Promise to wrap
   * @param timeoutMs - Timeout in milliseconds
   * @param signal - Optional abort signal
   * @returns Promise that rejects after timeout or abort
   */
  private withTimeout<T>(
    promise: Promise<T>,
    timeoutMs: number,
    signal?: AbortSignal
  ): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      // Check if already aborted (abort takes priority)
      if (signal?.aborted) {
        reject(new Error('ABORTED'));
        return;
      }

      const timer = setTimeout(() => reject(new Error('TIMEOUT')), timeoutMs);

      // Cleanup helper (clears timer and removes abort listener)
      const settle = (fn: () => void) => {
        clearTimeout(timer);
        if (signal) {
          signal.removeEventListener('abort', onAbort);
        }
        fn();
      };

      // Abort handler (abort takes priority over timeout)
      const onAbort = () => settle(() => reject(new Error('ABORTED')));

      // Attach abort listener
      if (signal) {
        signal.addEventListener('abort', onAbort, { once: true });
      }

      // Settle on promise resolution/rejection
      promise.then(
        (value) => settle(() => resolve(value)),
        (error) => settle(() => reject(error))
      );
    });
  }

  /**
   * Get random number for jitter calculation
   * Injectable for deterministic tests (tests may monkey-patch this method)
   * @returns Random number [0, 1)
   */
  private getRandom(): number {
    return Math.random();
  }

  /**
   * Check if user has snoozed reconnection banner for provider
   * Key format: sync:snooze:<provider>:<userId8>
   *
   * @param provider - Provider type
   * @param userId - User ID (will be truncated to 8 chars)
   * @returns True if snoozed, false otherwise (safe for SSR)
   */
  private isBannerSnoozed(provider: string, userId: string): boolean {
    try {
      const userIdShort = userId.slice(0, 8);
      const key = `sync:snooze:${provider}:${userIdShort}`;
      return localStorage.getItem(key) === '1';
    } catch {
      // SSR or localStorage unavailable
      return false;
    }
  }

  /**
   * Attempt silent token refresh for Google Drive
   * STUB for 2.B - real implementation deferred to 2.D or Edge Function
   *
   * @param _provider - Google Drive provider instance (unused for stub)
   * @returns Promise resolving to false (no refresh in 2.B)
   */
  private async trySilentRefresh(_provider: GoogleDriveSyncProvider): Promise<boolean> {
    // TODO: Task 2.D - implement real silent refresh or move to Edge Function
    return false;
  }

  /**
   * Retry connection attempt with exponential backoff + jitter
   *
   * Features:
   * - Exponential backoff: 5s, 10s, 20s, 40s (from SYNC_INIT_BACKOFF_MS config)
   * - ±20% jitter to prevent stampedes
   * - Respects AbortSignal for cancellation
   * - Checks snooze flag before retrying
   * - Short-circuits on offline (for network-dependent providers)
   * - Only one terminal outcome (no double resolve)
   *
   * @param fn - Function to retry
   * @param options - Retry options (signal, userId, provider, needsNetwork)
   * @param attempt - Current attempt number (1-indexed)
   * @returns ConnectionResult from successful attempt or final failure
   */
  private async retryWithBackoff(
    fn: () => Promise<ConnectionResult>,
    options: {
      signal?: AbortSignal;
      userId: string;
      provider: string;
      needsNetwork?: boolean; // Default true for most providers
    },
    attempt = 1
  ): Promise<ConnectionResult> {
    const { signal, userId, provider, needsNetwork = true } = options;
    const userIdShort = userId.slice(0, 8);

    // Check abort signal
    if (signal?.aborted) {
      return { success: false, reason: 'ABORTED', shouldRetry: false };
    }

    // Check snooze flag
    if (this.isBannerSnoozed(provider, userId)) {
      console.log(`⏸️ Retry snoozed by user for ${provider} (user: ${userIdShort})`);
      return {
        success: false,
        reason: 'UNKNOWN_ERROR',
        snoozed: true, // Mark for telemetry/UX - user explicitly dismissed
        shouldRetry: false,
      };
    }

    // Short-circuit if offline (for network-dependent providers only)
    if (needsNetwork && !isOnline()) {
      return { success: false, reason: 'OFFLINE', shouldRetry: true };
    }

    // Execute attempt
    const result = await fn();

    // Stop retrying if:
    // - Success
    // - Modal required (user action needed - folder missing, token revoked, etc.)
    // - Reason not retryable
    // - Snoozed by user
    // - Max attempts reached
    if (
      result.success ||
      (!result.success && result.modalRequired) ||
      (!result.success && result.snoozed) ||
      !shouldRetryReason(result.reason) ||
      attempt >= SYNC_INIT_MAX_RETRIES
    ) {
      return result;
    }

    // Calculate delay: prefer retryAfterMs (429) over exponential backoff
    let delayMs: number;
    if (!result.success && result.retryAfterMs) {
      // Use Retry-After header from 429 response
      delayMs = result.retryAfterMs;
      console.log(
        `⏱️ [${provider}] Rate limited, retry in ${delayMs}ms (Retry-After) (user: ${userIdShort})`
      );
    } else {
      // Use exponential backoff with jitter
      const baseDelay = SYNC_INIT_BACKOFF_MS[attempt - 1] ?? 5000; // Fallback to 5s if undefined
      const jitter = this.getRandom() * 0.4; // 0 to 0.4
      delayMs = Math.round(baseDelay * (0.8 + jitter)); // 0.8x to 1.2x
      console.log(
        `⏱️ [${provider}] Retry ${attempt}/${SYNC_INIT_MAX_RETRIES} in ${delayMs}ms (user: ${userIdShort})`
      );
    }

    // Wait with abort support
    try {
      await this.wait(delayMs, signal);
    } catch (_error) {
      // Aborted during wait
      return { success: false, reason: 'ABORTED', shouldRetry: false };
    }

    // Recursive retry
    return this.retryWithBackoff(fn, options, attempt + 1);
  }

  /**
   * Wait with AbortSignal support
   * Properly cleans up timer on all exit paths (resolve, reject, abort)
   *
   * @param ms - Milliseconds to wait
   * @param signal - Optional abort signal
   */
  private wait(ms: number, signal?: AbortSignal): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      const timer = setTimeout(() => {
        cleanup();
        resolve();
      }, ms);

      const onAbort = () => {
        cleanup();
        reject(new Error('ABORTED'));
      };

      const cleanup = () => {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
      };

      if (signal) {
        signal.addEventListener('abort', onAbort, { once: true });
      }
    });
  }
}

// ===== SINGLETON EXPORT =====

/**
 * Singleton instance of SyncInitializationService
 *
 * IMPORTANT: Use this shared instance instead of creating new instances.
 * This ensures idempotency and prevents duplicate initialization attempts.
 */
export const syncInitializationService = SyncInitializationService.getInstance();
